'use client';

import React, { useEffect, useMemo, useState } from 'react'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { Link } from '@/i18n/navigation';
import smoothScrollToElement from '@/utils/smoothScroll';
import { useModal } from "@/contexts/ModalContext";
import { createPortal } from "react-dom";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useLocale } from "next-intl";
import clsx from 'clsx';
import { useTranslationDetector } from '@/hooks/useTranslationDetector';
import { useHotelFavorite } from '@/hooks/useHotelFavorite';
import { buildFromDetailData } from '@/utils/hotel/buildFavoritedHotelPayload';
import { useHotelAddToWishlistAnalytics } from '@/components/pages/hotel/search/hooks/useHotelAddToWishlistAnalytics';
import { useHotelMapViewAnalytics } from '@/components/pages/hotel/search/hooks/useHotelMapViewAnalytics';
import { getHotelListContext } from '@/analytics/services/hotel/hotelListContextStorage';
import HotelSharePopup from "./HotelSharePopup";
import { useHotelShareAnalytics } from "./hooks/useHotelShareAnalytics";

const STATIC_LATITUDE = "41.0082";
const STATIC_LONGITUDE = "28.9784";

export default function Header({
    hotelData,
    checkinDate,
    checkOutDate,
    roomsData,
    guests = {},
    productForAnalytics,
    passengersForAnalytics,
}) {
    const t = useTranslationDetector('hotel');
    const hotelName = hotelData?.HotelName || hotelData?.Name || '';
    const starNumber = hotelData?.StarNumber || 0;
    const address = hotelData?.Address || '';
    const city = hotelData?.City || '';
    const fullAddress = address && city ? `${address}, ${city}` : city || address || '';

    const stars = Array(Math.min(Math.max(starNumber, 0), 5)).fill(0);

    const [isMapExpanded, setIsMapExpanded] = useState(false);
    const [isMounted, setIsMounted] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const mapModal = useModal("hotel-map-modal");
    const { currency } = useCurrency();
    const locale = useLocale();

    const favoriteContext = useMemo(
        () => ({
            locale: locale ?? "tr",
            currency: currency ?? "TRY",
            checkinDate,
            checkoutDate: checkOutDate,
            roomsData,
            adultCount: guests?.adult ?? 1,
            childCount: guests?.child ?? 0,
            childrenAges: guests?.childrenAges ?? [],
        }),
        [locale, currency, checkinDate, checkOutDate, roomsData, guests?.adult, guests?.child, guests?.childrenAges?.join?.(",")]
    );
    const listContext = getHotelListContext();
    const wishlistItemListId = listContext?.item_list_id ?? "hotel_detail";
    const wishlistItemListName = listContext?.item_list_name ?? "Hotel Detail";

    const { trackAddToWishlist } = useHotelAddToWishlistAnalytics({
        product: productForAnalytics,
        itemListId: wishlistItemListId,
        itemListName: wishlistItemListName,
        passengers: passengersForAnalytics,
        currency: productForAnalytics?.Currency ?? undefined,
        value: productForAnalytics?.price ?? undefined,
    });

    const details = hotelData?.HotelDetails ?? {};
    const isDomestic = hotelData?.IsDomestic ?? hotelData?.Country === "TR";
    const { trackHotelMapView } = useHotelMapViewAnalytics({
        hotel_city: details.City ?? hotelData?.City ?? null,
        hotel_district: details.District ?? hotelData?.District ?? null,
        hotel_neighborhood: details.Neighborhood ?? hotelData?.Neighborhood ?? null,
        hotel_country: details.CountryName ?? (hotelData?.Country === "TR" ? "Türkiye" : hotelData?.CountryName ?? hotelData?.Country) ?? null,
        hotel_region: details.Region ?? hotelData?.Region ?? hotelData?.RegionName ?? null,
        state: isDomestic ? null : (details.State ?? hotelData?.State ?? null),
        guest_total: (guests?.adult ?? 1) + (guests?.child ?? 0),
        guest_adult_number: guests?.adult ?? 1,
        guest_children_number: guests?.child ?? 0,
        start_date: checkinDate ?? null,
        end_date: checkOutDate ?? null,
    });

    const { isFavorite, isAddingFavorite, handleFavoriteClick } = useHotelFavorite({
        hotelData,
        getPayload: buildFromDetailData,
        context: favoriteContext,
        hotelIdKey: "HotelId",
    });

    // API'den gelen koordinatları kullan, yoksa default değerleri kullan
    const lat = hotelData?.Latitude || STATIC_LATITUDE;
    const lng = hotelData?.Longitude || STATIC_LONGITUDE;

    // Türkçe formatındaki koordinatları (virgülle ayrılmış) nokta ile değiştir
    const normalizedLat = lat.toString().replace(',', '.');
    const normalizedLng = lng.toString().replace(',', '.');

    const mapSrc = `https://maps.google.com/?q=${normalizedLat},${normalizedLng}&output=embed`;

    useEffect(() => {
        setIsMounted(true);

        // Mobil kontrolü için window width kontrolü
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768); // md breakpoint
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    useEffect(() => {
        if (!isMapExpanded) {
            return undefined;
        }
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalOverflow;
        };
    }, [isMapExpanded]);

    const handleReservationClick = () => {
        const roomsElement = document.getElementById('rooms');
        if (roomsElement) {
            smoothScrollToElement(roomsElement, 108, 0);
        }
    };

    const handleMapClick = () => {
        trackHotelMapView();
        // Mobilde modal aç, desktop'ta expanded modal aç
        if (isMobile) {
            mapModal.open({
                title: hotelName,
                size: "full",
                isBottomSheet: true,
                isFullScreen: false,
                contentClassName: "p-0",
            });
        } else {
            setIsMapExpanded(true);
        }
    };

    const handleMapClose = () => {
        setIsMapExpanded(false);
    };

    const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
    const { trackShareHotel } = useHotelShareAnalytics();

    const expandedMapContent =
        isMounted && isMapExpanded && !isMobile
            ? createPortal(
                <div
                    className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 p-4"
                    role="dialog"
                    aria-modal="true"
                    onClick={(event) => {
                        if (event.target === event.currentTarget) {
                            handleMapClose();
                        }
                    }}
                >
                    <div className="relative w-full max-w-[803px] h-[540px] overflow-hidden rounded-lg bg-white ">
                        <div className="flex flex-col gap-6 p-6">
                            <div className="flex items-center justify-between border-border ">
                                <h2 className="text-xl font-semibold text-foreground">
                                    {hotelName}
                                </h2>
                                <button
                                    type="button"
                                    onClick={handleMapClose}
                                    aria-label={t('closeMapPreview')}
                                    className="cursor-pointer justify-center text-foreground-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                    <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} fontSize={24} />
                                </button>
                            </div>
                            <div className="">
                                <iframe
                                    className="w-full h-[432px]"
                                    src={mapSrc}
                                    title="Hotel location expanded"
                                    loading="lazy"
                                    allowFullScreen
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>
                    </div>
                </div>,
                document.body,
            )
            : null;

    return (
        <div className='flex items-center justify-between'>
            <div className='flex flex-col gap-2 md:gap-1'>
                <div className='md:flex md:gap-[10px] md:items-center md:flex-1'>
                    <h1 className='md:flex md:items-center md:gap-[10px] md:flex-1'>
                        <span className='text-[23px] leading-[30px] sm:text-[26px] sm:leading-[34px] text-foreground font-semibold'>{hotelName}</span>
                        {stars.length > 0 && (
                            <span className='flex gap-[1px] items-center mt-1 md:mt-0'>
                                {stars.map((item, index) => (
                                    <span key={index} className='!w-3.5 h-3.5 flex items-center justify-center'>
                                        <FontAwesomeIcon key={index} className='text-yellow !w-[12px] !h-[11px]' icon={byPrefixAndName.fas['star']} />
                                    </span>
                                ))}
                            </span>
                        )}
                    </h1>

                </div>
                {fullAddress && (
                    <div className='flex items-center gap-1 md:gap-2 flex-wrap'>
                        <div className='flex gap-[6px] md:gap-1 items-start'>
                            <span className='!w-5 !h-5 flex items-center justify-center'>
                                <FontAwesomeIcon className='text-foreground-muted !w-[11.66px] h-[15px]' icon={byPrefixAndName.far['location-dot']} />
                            </span>
                            <span className='text-foreground-muted leading-19 text-sm sm:text-base'>{fullAddress}</span>
                        </div>
                        <span
                            onClick={handleMapClick}
                            className='hidden sm:block text-foreground font-medium text-sm leading-19 sm:text-base sm:leading-21 cursor-pointer'
                        >
                            {t('showOnMap')}
                        </span>
                    </div>
                )}
            </div>
            <div className='md:flex gap-6 items-center hidden'>
                <button
                    type="button"
                    onClick={(e) => {
                        if (!isFavorite) trackAddToWishlist();
                        handleFavoriteClick(e);
                    }}
                    disabled={isAddingFavorite}
                    className="cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={isFavorite ? t('removeFromFavorites') : t('addToFavorites')}
                >
                    <span className='!w-6 !h-6 flex items-center justify-center'>
                        <FontAwesomeIcon
                            className={
                                clsx(
                                    isFavorite ? 'text-danger' : 'text-foreground',
                                    "!w-[19.2px] !h-[16.4px]"
                                )
                            }
                            icon={isFavorite ? byPrefixAndName.fas['heart'] : byPrefixAndName.far['heart']}
                        />
                    </span>
                </button>
                <button
                    type="button"
                    onClick={() => {
                        trackShareHotel(hotelName);
                        setIsSharePopupOpen(true);
                    }}
                    className='!w-6 !h-6 flex items-center justify-center cursor-pointer'
                    aria-label={t('shareHotel')}
                >
                    <FontAwesomeIcon className={clsx(
                        "text-foreground",
                        "!w-[19px] !h-[19px]"
                    )} icon={byPrefixAndName.far['share-nodes']} />
                </button>
                <button
                    onClick={handleReservationClick}
                    className='text-base font-medium text-white bg-primary h-[50px] px-6 rounded-lg cursor-pointer whitespace-nowrap'
                >
                    {t('makeReservation')}
                </button>
            </div>
            <HotelSharePopup
                isOpen={isSharePopupOpen}
                onClose={() => setIsSharePopupOpen(false)}
                hotelData={hotelData}
            />
            {expandedMapContent}
        </div>
    )
}
